/**
 * Bank Reconciliation Screen
 * Match and reconcile bank transactions with book entries
 */

import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, Download, Calendar, Building2, Check, X as XIcon,
  CheckCircle, AlertCircle, Clock, Filter, Search
} from 'lucide-react';
import api from '@/services/api';

const BankReconciliationScreen = ({ theme }) => {
  const isDark = theme === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBank, setSelectedBank] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [filter, setFilter] = useState('all'); // 'all' | 'cleared' | 'pending'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedBank, fromDate, toDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/api/reports/bank-reconciliation?';
      if (selectedBank) url += `bankCode=${selectedBank}&`;
      if (fromDate) url += `fromDate=${fromDate}&`;
      if (toDate) url += `toDate=${toDate}&`;
      
      const response = await api.get(url);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching bank reconciliation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearTransaction = async (tranno) => {
    try {
      await api.post('/api/reports/bank-reconciliation/clear', {
        tranno,
        clearDate: new Date().toISOString()
      });
      fetchData();
    } catch (error) {
      console.error('Error clearing transaction:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getFilteredEntries = () => {
    let entries = data?.entries || [];
    
    if (filter === 'cleared') {
      entries = entries.filter(e => e.Cleared === 'Y');
    } else if (filter === 'pending') {
      entries = entries.filter(e => e.Cleared !== 'Y');
    }
    
    if (searchTerm) {
      entries = entries.filter(e => 
        e.tranno?.toString().includes(searchTerm) ||
        e.voucherno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.particulars?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return entries;
  };

  const filteredEntries = getFilteredEntries();

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`p-4 border-b ${isDark ? 'border-[#222] bg-[#111]' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-600'}`}>
              <Building2 size={20} />
            </div>
            <div>
              <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Bank Reconciliation</h1>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Match bank statements with book entries
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchData}
              data-testid="refresh-brs-btn"
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <RefreshCw size={18} />
            </button>
            <button 
              data-testid="export-brs-btn"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <Download size={16} /> Export
            </button>
          </div>
        </div>
        
        {/* Controls */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {/* Bank Selector */}
          <select
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            data-testid="bank-selector"
            className={`px-3 py-1.5 rounded-lg border text-sm ${
              isDark 
                ? 'bg-[#1a1a1a] border-[#333] text-white' 
                : 'bg-white border-gray-200 text-gray-900'
            }`}
          >
            <option value="">All Banks</option>
            {data?.banks?.map(bank => (
              <option key={bank.GLId} value={bank.GLId}>{bank.GLName}</option>
            ))}
          </select>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              placeholder="From"
              data-testid="brs-from-date"
              className={`px-3 py-1.5 rounded-lg border text-sm ${
                isDark 
                  ? 'bg-[#1a1a1a] border-[#333] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
            />
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="brs-to-date"
              className={`px-3 py-1.5 rounded-lg border text-sm ${
                isDark 
                  ? 'bg-[#1a1a1a] border-[#333] text-white' 
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
            />
          </div>
          
          {/* Status Filter */}
          <div className={`flex p-0.5 rounded-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
            {['all', 'pending', 'cleared'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}-btn`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  filter === f 
                    ? 'bg-cyan-600 text-white' 
                    : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-white border-gray-200'}`}>
            <Search size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="brs-search-input"
              className={`bg-transparent text-sm outline-none w-32 ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className={`p-4 border-b ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#151515]' : 'bg-white border border-gray-200'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Book Balance</p>
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(data.summary.bookBalance)}
              </p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#151515]' : 'bg-white border border-gray-200'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Pending Balance</p>
              <p className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                {formatCurrency(data.summary.pendingBalance)}
              </p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#151515]' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-500" />
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Cleared</p>
              </div>
              <p className={`text-xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                {data.summary.clearedCount}
              </p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-[#151515]' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-amber-500" />
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Pending</p>
              </div>
              <p className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                {data.summary.pendingCount}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className={`text-center py-16 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Building2 size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No transactions found</p>
            <p className="text-sm">
              {data?.banks?.length === 0 
                ? 'No bank accounts configured' 
                : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
            <table className="w-full">
              <thead className={isDark ? 'bg-[#1a1a1a]' : 'bg-gray-50'}>
                <tr className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Reference</th>
                  <th className="text-left p-3">Particulars</th>
                  <th className="text-right p-3">Debit</th>
                  <th className="text-right p-3">Credit</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-center p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry, index) => (
                  <tr 
                    key={entry.tranno || index}
                    data-testid={`brs-row-${index}`}
                    className={`border-t ${isDark ? 'border-[#333] hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <td className={`p-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {entry.trandate ? new Date(entry.trandate).toLocaleDateString() : '-'}
                    </td>
                    <td className={`p-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <p className="font-medium text-sm">{entry.voucherno || entry.tranno}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {entry.chequeno && `Chq: ${entry.chequeno}`}
                      </p>
                    </td>
                    <td className={`p-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {entry.particulars || '-'}
                    </td>
                    <td className={`p-3 text-right font-mono text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      {entry.Dramount > 0 ? formatCurrency(entry.Dramount) : '-'}
                    </td>
                    <td className={`p-3 text-right font-mono text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                      {entry.Cramount > 0 ? formatCurrency(entry.Cramount) : '-'}
                    </td>
                    <td className="p-3 text-center">
                      {entry.Cleared === 'Y' ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                        }`}>
                          <CheckCircle size={12} /> Cleared
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                        }`}>
                          <Clock size={12} /> Pending
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {entry.Cleared !== 'Y' && (
                        <button
                          onClick={() => handleClearTransaction(entry.tranno)}
                          data-testid={`clear-btn-${index}`}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isDark 
                              ? 'hover:bg-green-500/20 text-green-400' 
                              : 'hover:bg-green-100 text-green-600'
                          }`}
                          title="Mark as Cleared"
                        >
                          <Check size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bank Selection Empty State */}
      {!loading && data?.banks?.length === 0 && (
        <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]/90' : 'bg-white/90'}`}>
          <div className="text-center max-w-md p-8">
            <Building2 size={64} className={`mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No Bank Accounts Found
            </h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Please ensure bank accounts are configured in the Chart of Accounts with GL Type as "BANK".
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankReconciliationScreen;
